import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { ProductListing } from "@/components/storefront/ProductListing";
import { BrandLogo } from "@/components/storefront/BrandLogo";
import { PlayfulError, PlayfulNotFound } from "@/components/storefront/PlayfulError";
import { listingSearchSchema } from "@/lib/listing-search";
import { getManufacturerByPsId } from "@/lib/products.functions";
import { idFromSlug } from "@/lib/format";

export const Route = createFileRoute("/brand/$id")({
  validateSearch: listingSearchSchema,
  loader: async ({ params }) => {
    const psId = Number(idFromSlug(params.id));
    if (!Number.isFinite(psId)) return { brandName: null as string | null };
    try {
      const res = await getManufacturerByPsId({ data: { psId } });
      return { brandName: res?.manufacturer?.name ?? null };
    } catch {
      return { brandName: null as string | null };
    }
  },
  head: ({ params, loaderData }) => {
    const brandName = (loaderData as { brandName?: string | null } | undefined)?.brandName ?? null;
    const name = brandName ?? "η μάρκα";
    const url = `https://mrpanda.gr/brand/${params.id}`;
    const title = brandName ? `${brandName} — Προϊόντα μάρκας | MrPanda.gr` : "Προϊόντα μάρκας — MrPanda.gr";
    const description = `Ανακάλυψε όλα τα προϊόντα ${name} στο MrPanda.gr σε χαμηλές τιμές, με ασφαλείς πληρωμές και γρήγορη παράδοση σε όλη την Ελλάδα.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            description,
            url,
            inLanguage: "el",
          }),
        },
      ],
    };
  },
  component: BrandPage,
  errorComponent: ({ error, reset }) => (
    <StorefrontLayout>
      <PlayfulError error={error instanceof Error ? error : new Error(String(error))} reset={reset} />
    </StorefrontLayout>
  ),
  notFoundComponent: () => (
    <StorefrontLayout>
      <PlayfulNotFound />
    </StorefrontLayout>
  ),
});

function BrandPage() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const psId = Number(idFromSlug(id));

  const { data } = useQuery({
    queryKey: ["manufacturer", psId],
    queryFn: () => getManufacturerByPsId({ data: { psId } }),
    enabled: Number.isFinite(psId),
    staleTime: 5 * 60_000,
  });
  const brand = data?.manufacturer;
  const brandName = brand?.name ?? "Μάρκα";

  return (
    <StorefrontLayout>
      <div className="mx-auto max-w-none px-4 pt-6 pb-10">
        <div className="mb-6 flex items-center gap-4 overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-accent via-accent/40 to-background p-5 animate-pop-in">
          {brand?.logo_url ? (
            <BrandLogo
              logoUrl={brand.logo_url}
              name={brandName}
              width={160}
              sizes="80px"
              className="h-20 w-20 shrink-0 rounded-xl p-2 text-3xl shadow-sm hover-scale"
            />
          ) : (
            <div className="grid h-20 w-20 shrink-0 place-items-center rounded-full bg-background text-5xl animate-bob">🐼</div>
          )}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">✨ Μάρκα</p>
            <h1 className="text-3xl font-bold">{brandName}</h1>
            <p className="mt-1 text-sm text-muted-foreground">Ανακάλυψε όλα τα προϊόντα 🐼</p>
          </div>
        </div>
        {Number.isFinite(psId) && (
          <ProductListing
            scope={{ mode: "all", manufacturerPsId: psId, cacheKey: `brand:${psId}` }}
            search={search}
            onChange={(next) => navigate({ search: (prev: typeof search) => ({ ...prev, ...next }), replace: true })}
          />
        )}
      </div>
    </StorefrontLayout>
  );
}